import axiosInstance from '../../config/axiosInstance';
import { isJwtExpired } from './tokenUtils';

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface AuthResponse {
    user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        role: string;
        profileImage?: string | null;
    };
    accessToken: string;
    refreshToken: string;
}

class JWTService {
    /**
     * Login with email and password - Admin only
     */
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        try {
            const response = await axiosInstance.post<AuthResponse>('/auth/login', credentials);

            // Validate admin role
            if (response.data.user.role !== 'admin') {
                throw new Error('Access denied. Only administrators can access this portal.');
            }

            // Store tokens in localStorage
            localStorage.setItem('adminAuth', JSON.stringify({
                user: response.data.user,
                token: response.data.accessToken,
                refreshToken: response.data.refreshToken,
            }));

            return response.data;
        } catch (error: any) {
            // Handle specific error messages
            if (error.message) {
                throw error;
            }
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error('Login failed. Please check your credentials and try again.');
        }
    }

    /**
     * Logout user
     */
    async logout(): Promise<void> {
        try {
            await axiosInstance.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always clear local storage
            localStorage.removeItem('adminAuth');
        }
    }

    /**
     * Check if user is authenticated as admin
     */
    isAuthenticated(): boolean {
        const authData = localStorage.getItem('adminAuth');
        if (!authData) return false;

        try {
            const { token, user } = JSON.parse(authData);
            return !!token && !isJwtExpired(token) && user?.role === 'admin';
        } catch (error) {
            return false;
        }
    }

    /**
     * Get stored token
     */
    getToken(): string | null {
        const authData = localStorage.getItem('adminAuth');
        if (!authData) return null;

        try {
            const { token } = JSON.parse(authData);
            if (!token || isJwtExpired(token)) {
                localStorage.removeItem('adminAuth');
                return null;
            }
            return token;
        } catch (error) {
            return null;
        }
    }

    /**
     * Get stored user
     */
    getUser(): AuthResponse['user'] | null {
        const authData = localStorage.getItem('adminAuth');
        if (!authData) return null;

        try {
            const { user } = JSON.parse(authData);
            return user?.role === 'admin' ? user : null;
        } catch (error) {
            return null;
        }
    }
}

// Export singleton instance
const jwtService = new JWTService();
export default jwtService;
